/**
 * The SaaS providers the status board covers (docs/03). Each row maps one or
 * more inbound `source_type` values to the name management recognises.
 */
export interface ProviderDefinition {
  key: string;
  label: string;
  /** Source types whose integrations count towards this provider. */
  sourceTypes: string[];
  description: string;
}

export const PROVIDERS: ProviderDefinition[] = [
  {
    key: 'm365',
    label: 'Microsoft 365',
    sourceTypes: ['m365_service_health'],
    description: 'Service health and Message Center',
  },
  {
    key: 'azure',
    label: 'Azure',
    sourceTypes: ['azure_service_health', 'azure_monitor'],
    description: 'Service health, Resource Health and Monitor alerts',
  },
  {
    key: 'entra',
    label: 'Microsoft Entra',
    sourceTypes: ['entra_health', 'entra_risk_detections'],
    description: 'Identity health and risk detections',
  },
  {
    key: 'teams_rooms',
    label: 'Teams Rooms Pro',
    sourceTypes: ['teams_rooms_pro'],
    description: 'Room and device incidents',
  },
  {
    key: 'logitech',
    label: 'Logitech Sync',
    sourceTypes: ['logitech_sync'],
    description: 'Room devices and peripherals',
  },
  {
    key: 'statuspages',
    label: 'Status pages',
    sourceTypes: ['statuspage', 'slack_status', 'gcp_status', 'rss', 'azure_public_status'],
    description: 'Atlassian Statuspage, Slack, Google Cloud, the Azure banner and feeds',
  },
];

export type ProviderHealth = 'healthy' | 'degraded' | 'failing' | 'disabled' | 'not_configured';

export const PROVIDER_HEALTH_TOKENS: Record<ProviderHealth, { ink: string; label: string }> = {
  healthy: { ink: 'var(--success)', label: 'Healthy' },
  degraded: { ink: 'var(--warn)', label: 'Degraded' },
  failing: { ink: 'var(--danger)', label: 'Failing' },
  disabled: { ink: 'var(--text-3)', label: 'Disabled' },
  not_configured: { ink: 'var(--text-3)', label: 'Not configured' },
};

export interface ProviderStatus {
  provider: ProviderDefinition;
  health: ProviderHealth;
  /** Most recent successful poll across the provider's integrations. */
  lastPollAt: string | null;
  integrationCount: number;
  enabledCount: number;
  failingCount: number;
  lastError: string | null;
  activeIncidents: number;
}

interface IntegrationLike {
  id: string;
  source_type: string;
  enabled: boolean;
  last_poll_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
}

/** Roll integrations and open alarms up into one tile per provider. */
export function buildProviderStatuses(
  integrations: IntegrationLike[],
  openAlarmsByIntegration: Map<string, number>,
): ProviderStatus[] {
  return PROVIDERS.map((provider) => {
    const owned = integrations.filter((i) => provider.sourceTypes.includes(i.source_type));
    const enabled = owned.filter((i) => i.enabled);
    const failing = enabled.filter((i) => i.consecutive_failures > 0 || i.last_error);
    const lastPollAt = enabled
      .map((i) => i.last_poll_at)
      .filter((value): value is string => Boolean(value))
      .sort()
      .at(-1);
    const activeIncidents = owned.reduce(
      (total, i) => total + (openAlarmsByIntegration.get(i.id) ?? 0),
      0,
    );

    let health: ProviderHealth;
    if (owned.length === 0) health = 'not_configured';
    else if (enabled.length === 0) health = 'disabled';
    else if (failing.some((i) => i.consecutive_failures >= 3)) health = 'failing';
    else if (failing.length > 0 || activeIncidents > 0) health = 'degraded';
    else health = 'healthy';

    return {
      provider,
      health,
      lastPollAt: lastPollAt ?? null,
      integrationCount: owned.length,
      enabledCount: enabled.length,
      failingCount: failing.length,
      lastError: failing[0]?.last_error ?? null,
      activeIncidents,
    };
  });
}
